import * as React from 'react'
import type { GeoPoint, SiteBoundary } from '@lib/dto/geo.dto'
import { BOUNDARY_MAX_VERTICES, BOUNDARY_MIN_VERTICES } from '@lib/dto/geo.dto'
import {
  findBoundaryProblem,
  type BoundaryProblem,
} from '@lib/validators/polygon.validator'
import { polygonAreaAcres, polygonPerimeterMeters } from '@lib/helpers/geo'

export type BoundaryMode = 'view' | 'drawing' | 'editing'

export interface BoundaryDraft {
  vertices: GeoPoint[]
  mode: BoundaryMode
  selectedIndex: number | null
  /** The first structural problem with the ring, if any. */
  problem: BoundaryProblem | null
  isDirty: boolean
  canSave: boolean
  canUndo: boolean
  isFull: boolean
  areaAcres: number
  perimeterMeters: number

  startDrawing: () => void
  /**
   * Move the corners of the ring already on the draft.
   *
   * Pass a ring to open that outline. The vertices and the editing mode are
   * set together, so a draft whose saved ring is empty — an area, which keeps
   * its saved shapes elsewhere — does not get cleared by the idle effect
   * before editing begins.
   */
  startEditing: (ring?: readonly GeoPoint[]) => void
  cancel: () => void
  appendVertex: (point: GeoPoint) => void
  insertVertex: (afterIndex: number, point: GeoPoint) => void
  moveVertex: (index: number, point: GeoPoint) => void
  removeSelectedVertex: () => void
  selectVertex: (index: number | null) => void
  undo: () => void
  clear: () => void
  /** The value to send, once saved. Null means "no outline". */
  toSaveValue: () => SiteBoundary | null
  /** Adopt what the server now holds, ending the edit. */
  reset: (boundary: SiteBoundary | null) => void
}

function sameRing(a: GeoPoint[], b: GeoPoint[]): boolean {
  if (a.length !== b.length) return false
  return a.every((vertex, index) => {
    const other = b[index]
    return (
      other !== undefined &&
      vertex.latitude === other.latitude &&
      vertex.longitude === other.longitude
    )
  })
}

/**
 * The in-progress outline.
 *
 * Boundary editing is explicitly transactional, unlike the tree survey next
 * door, where every tap is committed immediately. The difference is not
 * inconsistency — it follows from what the two things are. A tree is an
 * observation, complete the moment it is recorded, and losing one to a dropped
 * connection is losing real work. A boundary is a single shape that is
 * meaningless half-drawn: there is no useful moment to save "three of the seven
 * corners of this park", and auto-saving intermediate states would mean the
 * stored outline was wrong for most of the time someone spent drawing it.
 *
 * So this holds a draft, tracks whether it differs from what is stored, and
 * only the caller's Save writes anything.
 */
export function useBoundaryDraft(saved: SiteBoundary | null): BoundaryDraft {
  const savedVertices = React.useMemo(() => saved?.vertices ?? [], [saved])

  const [vertices, setVertices] = React.useState<GeoPoint[]>(savedVertices)
  const [mode, setMode] = React.useState<BoundaryMode>('view')
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)
  const [history, setHistory] = React.useState<GeoPoint[][]>([])

  // Adopt the server's value whenever it changes underneath an idle editor.
  // Guarded on `mode` so a reload mid-edit cannot discard someone's drawing.
  React.useEffect(() => {
    if (mode !== 'view') return
    setVertices(savedVertices)
  }, [savedVertices, mode])

  const problem = React.useMemo(
    () =>
      vertices.length >= BOUNDARY_MIN_VERTICES
        ? findBoundaryProblem(vertices)
        : null,
    [vertices],
  )

  const areaAcres = React.useMemo(
    () => (vertices.length >= 3 ? polygonAreaAcres(vertices) : 0),
    [vertices],
  )
  const perimeterMeters = React.useMemo(
    () => (vertices.length >= 2 ? polygonPerimeterMeters(vertices) : 0),
    [vertices],
  )

  const isDirty = !sameRing(vertices, savedVertices)

  /** Push the current ring onto the undo stack before changing it. */
  const commit = React.useCallback(
    (next: (current: GeoPoint[]) => GeoPoint[]) => {
      setVertices((current) => {
        setHistory((stack) => [...stack.slice(-49), current])
        return next(current)
      })
    },
    [],
  )

  const startDrawing = React.useCallback(() => {
    setHistory([])
    setVertices([])
    setSelectedIndex(null)
    setMode('drawing')
  }, [])

  const startEditing = React.useCallback((ring?: readonly GeoPoint[]) => {
    setHistory([])
    setSelectedIndex(null)
    if (ring !== undefined) setVertices(ring.map((vertex) => ({ ...vertex })))
    setMode('editing')
  }, [])

  const cancel = React.useCallback(() => {
    setVertices(savedVertices)
    setHistory([])
    setSelectedIndex(null)
    setMode('view')
  }, [savedVertices])

  const appendVertex = React.useCallback(
    (point: GeoPoint) => {
      commit((current) =>
        current.length >= BOUNDARY_MAX_VERTICES ? current : [...current, point],
      )
    },
    [commit],
  )

  const insertVertex = React.useCallback(
    (afterIndex: number, point: GeoPoint) => {
      commit((current) => {
        if (current.length >= BOUNDARY_MAX_VERTICES) return current
        const next = [...current]
        next.splice(afterIndex + 1, 0, point)
        return next
      })
      // Select what was just created: the reason to insert a point is almost
      // always to then put it somewhere, and selecting it puts Remove within
      // reach for an insert that was a misfire.
      setSelectedIndex(afterIndex + 1)
    },
    [commit],
  )

  const moveVertex = React.useCallback(
    (index: number, point: GeoPoint) => {
      commit((current) =>
        current.map((vertex, at) => (at === index ? point : vertex)),
      )
    },
    [commit],
  )

  const removeSelectedVertex = React.useCallback(() => {
    if (selectedIndex === null) return
    commit((current) => current.filter((_, at) => at !== selectedIndex))
    setSelectedIndex(null)
  }, [commit, selectedIndex])

  const undo = React.useCallback(() => {
    setHistory((stack) => {
      const previous = stack[stack.length - 1]
      if (previous === undefined) return stack
      setVertices(previous)
      setSelectedIndex(null)
      return stack.slice(0, -1)
    })
  }, [])

  const clear = React.useCallback(() => {
    commit(() => [])
    setSelectedIndex(null)
  }, [commit])

  const reset = React.useCallback((boundary: SiteBoundary | null) => {
    setVertices(boundary?.vertices ?? [])
    setHistory([])
    setSelectedIndex(null)
    setMode('view')
  }, [])

  const toSaveValue = React.useCallback((): SiteBoundary | null => {
    if (vertices.length < BOUNDARY_MIN_VERTICES) return null
    return { vertices }
  }, [vertices])

  /*
   * Saving is allowed for a sound ring, and for an empty one — clearing an
   * outline is a legitimate edit. What is refused is the in-between: one or two
   * points is not a shape, and saving it as "no boundary" would quietly discard
   * what someone had started.
   */
  const hasEnough = vertices.length >= BOUNDARY_MIN_VERTICES
  const canSave =
    isDirty &&
    problem === null &&
    (hasEnough || (vertices.length === 0 && savedVertices.length > 0))

  return {
    vertices,
    mode,
    selectedIndex,
    problem,
    isDirty,
    canSave,
    canUndo: history.length > 0,
    isFull: vertices.length >= BOUNDARY_MAX_VERTICES,
    areaAcres,
    perimeterMeters,
    startDrawing,
    startEditing,
    cancel,
    appendVertex,
    insertVertex,
    moveVertex,
    removeSelectedVertex,
    selectVertex: setSelectedIndex,
    undo,
    clear,
    toSaveValue,
    reset,
  }
}
